import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  Building2, 
  Clock, 
  CheckCircle, 
  MessageSquare,
  Phone,
  Calendar,
  MapPin,
  Users,
  Briefcase,
  Star,
  TrendingUp,
  Eye,
  User,
  Home,
  Bell,
  Video,
  BookOpen,
  FileText,
  Play,
  Download
} from 'lucide-react';
import { 
  demoAgencies, 
  demoCandidateData, 
  demoFeedPosts,
  demoStats,
  simulateApiCall 
} from './demoData';
import DemoMessaggi from './DemoMessaggi';
import DemoNotifiche from './DemoNotifiche';
import DemoFeedVideo from './DemoFeedVideo';

const DemoCandidato = () => {
  const [agencies, setAgencies] = useState([]);
  const [stats, setStats] = useState(demoStats.candidato);
  const [loading, setLoading] = useState(true);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [feedbackNote, setFeedbackNote] = useState('');
  const [feedbackEsito, setFeedbackEsito] = useState('');
  const [feedPosts] = useState(demoFeedPosts);
  const [activeTab, setActiveTab] = useState('home');

  // Navigation items
  const navItems = [
    { key: 'home', icon: Home, label: 'Home' },
    { key: 'opportunita', icon: Briefcase, label: 'Opportunità' },
    { key: 'formazione', icon: BookOpen, label: 'Formazione' },
    { key: 'messaggi', icon: MessageSquare, label: 'Messaggi' },
    { key: 'notifiche', icon: Bell, label: 'Notifiche' },
    { key: 'feed', icon: Video, label: 'Feed' }
  ];

  // Simulazione caricamento dati
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const data = await simulateApiCall(demoAgencies, 600);
      setAgencies(data);
      setLoading(false);
    };
    loadData();
  }, []);

  const handleSonoStatoContattato = (agency) => {
    // Simula restrizione: solo se agenzia ha stato "chiamato"
    if (agency.stato_match !== 'chiamato' && agency.stato_match !== 'colloquio_fissato') {
      alert('Demo: Devi attendere che l\'agenzia ti contatti prima di poter lasciare un feedback.');
      return;
    }
    
    setSelectedAgency(agency);
    setShowFeedbackModal(true);
  };

  const handleFeedbackSubmit = () => {
    if (!feedbackEsito) {
      alert('Seleziona un esito prima di inviare il feedback.');
      return;
    }

    // Simulazione aggiornamento (solo frontend)
    const updatedAgencies = agencies.map(a => {
      if (a.id === selectedAgency.id) {
        let updatedAgency = { ...a };
        
        if (feedbackEsito === 'non_interessato') {
          updatedAgency.stato_match = 'non_interessato';
        }
        
        if (feedbackNote.trim()) {
          updatedAgency.nota_candidato = feedbackNote.trim();
        }
        
        return updatedAgency;
      }
      return a;
    });

    setAgencies(updatedAgencies);
    
    // Chiudi modal
    setShowFeedbackModal(false);
    setFeedbackNote('');
    setFeedbackEsito('');
    
    // Toast simulato
    console.log(`✅ Demo: Feedback inviato per ${selectedAgency.nome_agenzia}`);
  };

  const getStatusInfo = (agency) => {
    const statusMap = {
      'in_attesa_contatto': { 
        color: 'bg-yellow-100 text-yellow-800', 
        text: 'In attesa contatto',
        description: 'L\'agenzia ha ricevuto il tuo profilo e ti contatterà presto.'
      },
      'chiamato': { 
        color: 'bg-blue-100 text-blue-800', 
        text: 'Contattato',
        description: 'L\'agenzia ti ha contattato. Puoi lasciare un feedback sulla chiamata.'
      },
      'colloquio_fissato': { 
        color: 'bg-green-100 text-green-800', 
        text: 'Colloquio fissato',
        description: `Colloquio programmato per il ${agency.data_colloquio ? new Date(agency.data_colloquio).toLocaleDateString() : 'da definire'}.`
      },
      'inserito': { 
        color: 'bg-purple-100 text-purple-800', 
        text: 'Inserito',
        description: 'Congratulazioni! Sei stato selezionato per questa posizione.'
      },
      'non_interessato': { 
        color: 'bg-red-100 text-red-800', 
        text: 'Non interessato',
        description: 'Hai indicato di non essere interessato a questa opportunità.'
      }
    };
    
    return statusMap[agency.stato_match] || { 
      color: 'bg-gray-100 text-gray-800', 
      text: agency.stato_match,
      description: ''
    };
  };

  const canProvideFeedback = (agency) => {
    return agency.stato_match === 'chiamato' || agency.stato_match === 'colloquio_fissato';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Caricamento dashboard candidato...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-slate-50">
      {/* Header Candidato */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl flex items-center justify-center text-white font-bold text-lg">
                MV
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{demoCandidateData.nome} {demoCandidateData.cognome}</h1>
                <p className="text-gray-600">Dashboard Candidato • {demoCandidateData.zona_interesse}</p>
              </div>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex gap-2">
              {navItems.map((item) => {
                const IconComponent = item.icon;
                return (
                  <Button
                    key={item.key}
                    variant={activeTab === item.key ? 'default' : 'outline'}
                    onClick={() => setActiveTab(item.key)}
                    className="rounded-xl"
                  >
                    <IconComponent className="w-4 h-4 mr-2" />
                    {item.label}
                  </Button>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-24 md:pb-8">
        {activeTab === 'home' && (
          <div className="space-y-6">
            {/* Dashboard Home Content */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.opportunita_attive}</div>
                  <div className="text-sm opacity-90">Opportunità Attive</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <Phone className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.agenzie_interessate}</div>
                  <div className="text-sm opacity-90">Agenzie Interessate</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <Calendar className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.colloqui_fissati}</div>
                  <div className="text-sm opacity-90">Colloqui Fissati</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                <CardContent className="p-4 text-center">
                  <Star className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.tasso_risposta}%</div>
                  <div className="text-sm opacity-90">Tasso Risposta</div>
                </CardContent>
              </Card>
            </div>

            {/* Quick Actions */}
            <Card>
              <CardContent className="p-6">
                <h3 className="text-lg font-semibold mb-4">Azioni Rapide</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Button
                    onClick={() => setActiveTab('opportunita')}
                    className="h-20 flex flex-col items-center gap-2 bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100"
                    variant="outline"
                  >
                    <Briefcase className="w-6 h-6" />
                    <span>Opportunità</span>
                  </Button>
                  <Button
                    onClick={() => setActiveTab('messaggi')}
                    className="h-20 flex flex-col items-center gap-2 bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                    variant="outline"
                  >
                    <MessageSquare className="w-6 h-6" />
                    <span>Messaggi</span>
                  </Button>
                  <Button
                    onClick={() => setActiveTab('feed')}
                    className="h-20 flex flex-col items-center gap-2 bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100"
                    variant="outline"
                  >
                    <Video className="w-6 h-6" />
                    <span>Feed Agenzie</span>
                  </Button>
                  <Button
                    onClick={() => setActiveTab('notifiche')}
                    className="h-20 flex flex-col items-center gap-2 bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100"
                    variant="outline"
                  >
                    <Bell className="w-6 h-6" />
                    <span>Notifiche</span>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'opportunita' && (
          <div className="space-y-6">
            {/* Header KPI */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <TrendingUp className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.opportunita_attive}</div>
                  <div className="text-sm opacity-90">Opportunità Attive</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <Phone className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.agenzie_interessate}</div>
                  <div className="text-sm opacity-90">Agenzie Interessate</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white border-0">
                <CardContent className="p-4 text-center">
                  <Calendar className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.colloqui_fissati}</div>
                  <div className="text-sm opacity-90">Colloqui Fissati</div>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
                <CardContent className="p-4 text-center">
                  <Star className="w-6 h-6 mx-auto mb-2 opacity-90" />
                  <div className="text-2xl font-bold">{stats.tasso_risposta}%</div>
                  <div className="text-sm opacity-90">Tasso Risposta</div>
                </CardContent>
              </Card>
            </div>

            {/* Lista Opportunità */}
            <div className="space-y-4">
              <div className="flex items-center gap-3 mb-6">
                <Briefcase className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">Le tue opportunità</h2>
              </div>

              {agencies.map((agency) => {
                const statusInfo = getStatusInfo(agency);
                
                return (
                  <Card key={agency.id} className="hover:shadow-lg transition-shadow duration-200">
                    <CardContent className="p-6">
                      <div className="flex flex-col md:flex-row gap-4">
                        {/* Logo e Info Agenzia */}
                        <div className="flex items-start gap-4 flex-1">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                            {agency.logo}
                          </div>
                          
                          <div className="flex-1 space-y-3">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <h3 className="font-semibold text-lg">{agency.nome_agenzia}</h3>
                                <Badge className={statusInfo.color}>{statusInfo.text}</Badge>
                              </div>
                              
                              <p className="text-gray-600 text-sm">
                                {agency.descrizione}
                              </p>
                            </div>

                            {/* Descrizione Stato */}
                            <div className="bg-blue-50 p-3 rounded-lg">
                              <div className="flex items-start gap-2">
                                <Eye className="w-4 h-4 text-blue-600 mt-0.5" />
                                <div className="text-sm text-blue-700">
                                  <strong>Stato:</strong> {statusInfo.description}
                                </div>
                              </div>
                            </div>

                            {/* Info Aggiuntive */}
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-4 h-4" />
                                {agency.zona}
                              </div>
                              <div className="flex items-center gap-1">
                                <Star className="w-4 h-4" />
                                {agency.score}% compatibilità
                              </div>
                              <div className="flex items-center gap-1">
                                <Users className="w-4 h-4" />
                                {agency.posti_disponibili} posti disponibili
                              </div>
                              <div className="flex items-center gap-1">
                                <Briefcase className="w-4 h-4" />
                                {agency.tipo_contratto}
                              </div>
                            </div>

                            {/* Nota Candidato se presente */}
                            {agency.nota_candidato && (
                              <div className="bg-green-50 p-3 rounded-lg">
                                <div className="flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 text-green-600 mt-0.5" />
                                  <div className="text-sm text-green-700">
                                    <strong>Il tuo feedback:</strong> {agency.nota_candidato}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Azioni */}
                        <div className="flex flex-col gap-3 min-w-[200px]">
                          {agency.stato_match === 'in_attesa_contatto' && (
                            <div className="text-center p-3 bg-yellow-50 rounded-xl">
                              <Clock className="w-6 h-6 text-yellow-600 mx-auto mb-2" />
                              <div className="text-sm text-yellow-800 font-semibold">In attesa contatto</div>
                              <div className="text-xs text-yellow-600 mt-1">
                                L'agenzia ti contatterà presto
                              </div>
                            </div>
                          )}

                          {canProvideFeedback(agency) && !agency.nota_candidato && (
                            <Button
                              onClick={() => handleSonoStatoContattato(agency)}
                              className="bg-green-500 hover:bg-green-600 text-white rounded-xl flex items-center gap-2"
                            >
                              <CheckCircle className="w-4 h-4" />
                              Sono stato contattato
                            </Button>
                          )}

                          {agency.stato_match === 'colloquio_fissato' && (
                            <div className="text-center p-3 bg-green-50 rounded-xl">
                              <Calendar className="w-6 h-6 text-green-600 mx-auto mb-2" />
                              <div className="text-sm text-green-800 font-semibold">Colloquio fissato</div>
                              {agency.data_colloquio && (
                                <div className="text-xs text-green-600 mt-1">
                                  {new Date(agency.data_colloquio).toLocaleDateString()}
                                </div>
                              )}
                            </div>
                          )}

                          {agency.stato_match === 'inserito' && (
                            <div className="text-center p-3 bg-purple-50 rounded-xl">
                              <CheckCircle className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                              <div className="text-sm text-purple-800 font-semibold">Congratulazioni!</div>
                              <div className="text-xs text-purple-600 mt-1">
                                Sei stato selezionato
                              </div>
                            </div>
                          )}

                          {agency.stato_match === 'non_interessato' && (
                            <div className="text-center p-2 bg-red-50 rounded-xl">
                              <div className="text-sm text-red-800">Non interessato</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        )}

        {activeTab === 'messaggi' && (
          <DemoMessaggi userType="candidato" />
        )}

        {activeTab === 'notifiche' && (
          <DemoNotifiche userType="candidato" />
        )}

        {activeTab === 'formazione' && (
          <div className="space-y-6">
            {/* Header Formazione */}
            <Card className="border-0 shadow-lg rounded-2xl">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen className="w-6 h-6 text-blue-600" />
                  <h2 className="text-2xl font-bold text-gray-900">Formazione & Crescita</h2>
                </div>
                <p className="text-gray-600">
                  Accedi a contenuti esclusivi per accelerare la tua crescita professionale
                </p>
              </CardContent>
            </Card>

            {/* Contenuti Demo Formazione Candidato */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Articolo 1 */}
              <Card className="border-0 shadow-lg rounded-2xl hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1560264280-88b68371db39?w=800" 
                    alt="Primo Colloquio"
                    className="w-full h-full object-cover"
                  />
                  <Badge className="absolute top-3 right-3 bg-blue-500 text-white border-0">
                    <FileText className="w-3 h-3 mr-1" />
                    Articolo
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">
                    Come Affrontare il Primo Colloquio
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Guida completa per prepararti al meglio al tuo primo colloquio con un'agenzia immobiliare.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">#colloquio</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">#preparazione</span>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl">
                    Leggi Articolo
                  </Button>
                </CardContent>
              </Card>

              {/* Video */}
              <Card className="border-0 shadow-lg rounded-2xl hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative h-48 bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                  <Play className="w-16 h-16 text-white" />
                  <Badge className="absolute top-3 right-3 bg-purple-500 text-white border-0">
                    <Video className="w-3 h-3 mr-1" />
                    Video
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">
                    Tecniche di Vendita Efficaci
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Guarda questo video per scoprire le migliori tecniche di vendita nel settore immobiliare.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded">#vendita</span>
                    <span className="text-xs bg-purple-50 text-purple-600 px-2 py-1 rounded">#tecniche</span>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-xl">
                    <Play className="w-4 h-4 mr-2" />
                    Guarda Video
                  </Button>
                </CardContent>
              </Card>

              {/* Articolo 2 */}
              <Card className="border-0 shadow-lg rounded-2xl hover:shadow-xl transition-shadow overflow-hidden">
                <div className="relative h-48">
                  <img 
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800" 
                    alt="Mindset"
                    className="w-full h-full object-cover"
                  />
                  <Badge className="absolute top-3 right-3 bg-blue-500 text-white border-0">
                    <FileText className="w-3 h-3 mr-1" />
                    Articolo
                  </Badge>
                </div>
                <CardContent className="p-6">
                  <h3 className="font-bold text-lg mb-2">
                    Mindset Vincente per Agenti
                  </h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Sviluppa la mentalità giusta per avere successo nel settore immobiliare.
                  </p>
                  <div className="flex gap-2 mb-4">
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">#mindset</span>
                    <span className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">#motivazione</span>
                  </div>
                  <Button className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl">
                    Leggi Articolo
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {activeTab === 'feed' && (
          <DemoFeedVideo userType="candidato" />
        )}
      </div>

      {/* Modal Feedback */}
      {showFeedbackModal && selectedAgency && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">
              Feedback per {selectedAgency.nome_agenzia}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-3 block">
                  Come è andata la chiamata?
                </label>
                
                <div className="space-y-2">
                  <label className="flex items-center p-3 border rounded-xl cursor-pointer hover:bg-green-50">
                    <input
                      type="radio"
                      name="esito"
                      value="procedere"
                      checked={feedbackEsito === 'procedere'}
                      onChange={(e) => setFeedbackEsito(e.target.value)}
                      className="mr-3"
                    />
                    <CheckCircle className="w-5 h-5 text-green-600 mr-2" />
                    <span className="text-green-800 font-semibold">Voglio procedere</span>
                  </label>
                  
                  <label className="flex items-center p-3 border rounded-xl cursor-pointer hover:bg-red-50">
                    <input
                      type="radio"
                      name="esito"
                      value="non_interessato"
                      checked={feedbackEsito === 'non_interessato'}
                      onChange={(e) => setFeedbackEsito(e.target.value)}
                      className="mr-3"
                    />
                    <Building2 className="w-5 h-5 text-red-600 mr-2" />
                    <span className="text-red-800 font-semibold">Non interessato dopo la chiamata</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="text-sm text-gray-600 mb-2 block">
                  Lascia una nota (facoltativo):
                </label>
                <textarea
                  value={feedbackNote}
                  onChange={(e) => setFeedbackNote(e.target.value)}
                  className="w-full p-3 border rounded-xl resize-none"
                  rows="3"
                  placeholder="Condividi la tua esperienza con questa agenzia..."
                />
              </div>

              <div className="bg-blue-50 p-3 rounded-lg">
                <div className="text-xs text-blue-700">
                  <strong>Nota:</strong> Il tuo feedback aiuterà a migliorare il servizio di matching
                  e sarà condiviso con l'agenzia e l'amministrazione.
                </div>
              </div>
            </div>

            <div className="flex gap-2 mt-6">
              <Button
                onClick={() => {
                  setShowFeedbackModal(false);
                  setFeedbackNote('');
                  setFeedbackEsito('');
                }}
                variant="outline"
                className="flex-1 rounded-xl"
              >
                Annulla
              </Button>
              <Button
                onClick={handleFeedbackSubmit}
                disabled={!feedbackEsito}
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white rounded-xl disabled:opacity-50"
              >
                Invia Feedback
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-2 z-40">
        <div className="flex justify-around items-center">
          {navItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <button
                key={item.key}
                onClick={() => setActiveTab(item.key)}
                className={`flex flex-col items-center gap-1 py-2 px-3 rounded-lg transition-colors ${
                  activeTab === item.key
                    ? 'text-blue-600 bg-blue-50'
                    : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                }`}
              >
                <IconComponent className="w-5 h-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DemoCandidato;